import React, {
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { Box, Image, Tooltip, useBreakpointValue } from "@chakra-ui/react";
import type { PlacementWithLogical } from "@chakra-ui/react";
import PathPickAiImageBadge from "@/features/pathpick/components/product/PathPickAiImageBadge";

const ZOOM_IMAGE_SIZE = "400px";

interface Props {
  imageUrl?: string;
  alt: string;
  // SKU 로 모인 AI 수집 이미지면 확대 이미지에도 배지를 달아 상품 자체 이미지와 구분한다.
  isAiCollected?: boolean;
  placement?: PlacementWithLogical;
  // 지나가는 마우스에는 뜨지 않게 하고 싶은 곳(카드 그리드)에서 지연을 준다.
  openDelay?: number;
  // 클릭으로 확대 이미지를 고정/해제한다. 카드 그리드의 돋보기처럼 트리거가
  // 명확한 버튼일 때만 켠다(모달 썸네일은 hover 전용으로 둔다).
  isClickToPinEnabled?: boolean;
  children: React.ReactNode;
}

/**
 * 상품 이미지를 hover 하면 확대 이미지를 띄우는 툴팁.
 *
 * 모달(ModalContent)에 overflow: hidden 이 걸려 있어 position: absolute 오버레이는
 * 잘리므로, Portal 로 렌더되는 Tooltip 에 label 로 확대 이미지를 넣는다.
 *
 * isClickToPinEnabled 면 클릭으로 확대 이미지를 고정할 수 있다 — 마우스를 떼도
 * 열린 채로 남고, 다시 클릭하거나 확대 이미지·바깥을 클릭하거나 Esc 로 닫는다.
 *
 * 좁은 폭(md 미만)에서는 확대를 제공하지 않는다 — 400px 확대 이미지가 뷰포트보다
 * 넓어 페이지 전체에 가로 스크롤이 생기고, 카드 이미지가 이미 화면 폭에 가까워 확대할 이득도 없다.
 *
 * 주의: children(트리거)에 fallbackSrc 로 엘리먼트가 교체되는 Image 를 직접 두면
 * Tooltip 의 ref 가 끊긴다 — Box 등으로 한 번 감싸서 넘긴다. 클릭 고정도 트리거
 * ref 로 바깥 클릭을 가려내므로 같은 제약을 따른다.
 */
export default function PathPickProductImageZoomTooltip({
  imageUrl,
  alt,
  isAiCollected = false,
  placement = "right",
  openDelay,
  isClickToPinEnabled = false,
  children,
}: Props) {
  // 클릭으로 고정한 상태와 hover 상태를 나눠 둔다 — 고정 중에는 마우스가 떠나도
  // (hover 가 false 가 돼도) 툴팁이 열려 있어야 한다.
  const [isPinned, setIsPinned] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  // 가로 스크롤은 뷰포트 폭 문제라 UA가 아니라 브레이크포인트로 가른다.
  const isNarrow = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );

  const closePin = () => {
    setIsPinned(false);
    // 고정을 풀 때 포인터가 아직 아이콘 위면 hover 가 true 로 남아 툴팁이 닫히지
    // 않는다 — 같이 내린다. (다시 띄우려면 한 번 벗어났다 들어와야 한다)
    setIsHovered(false);
  };

  useEffect(() => {
    if (!isPinned) {
      return;
    }

    // capture 단계로 잡아야 다른 카드 돋보기의 stopPropagation 보다 먼저 실행돼,
    // 400px 확대 이미지가 두 장 동시에 떠 있는 상황을 막는다.
    const handleDocumentClick = (event: MouseEvent) => {
      // 자기 트리거 클릭은 아래 onClick 토글이 처리한다.
      if (triggerRef.current?.contains(event.target as Node)) {
        return;
      }
      closePin();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closePin();
      }
    };

    document.addEventListener("click", handleDocumentClick, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isPinned]);

  // Tooltip 은 자식을 cloneElement 하면서 기존 onClick·ref 를 병합해 주므로,
  // 여기서 한 번 더 감싸 붙여도 hover 동작은 그대로 유지된다.
  const trigger =
    isClickToPinEnabled &&
    isValidElement<{ onClick?: React.MouseEventHandler }>(children)
      ? cloneElement(children, {
          ref: triggerRef,
          onClick: (event: React.MouseEvent) => {
            // 카드 클릭(상품 선택)으로도, 위 바깥 클릭 감지로도 새지 않게 끊는다.
            event.stopPropagation();
            children.props.onClick?.(event);
            if (isPinned) {
              closePin();
            } else {
              setIsPinned(true);
            }
          },
        } as React.Attributes)
      : children;

  return (
    <Tooltip
      placement={placement}
      hasArrow={true}
      openDelay={openDelay}
      // 이미지가 없으면(플레이스홀더) 확대할 게 없어 툴팁을 끈다. 좁은 폭에서도 끈다.
      isDisabled={!imageUrl || Boolean(isNarrow)}
      // 클릭 고정을 쓸 때만 제어 모드로 바꾼다 — 그 외 호출부는 기존 hover 그대로.
      {...(isClickToPinEnabled
        ? {
            isOpen: isPinned || isHovered,
            onOpen: () => setIsHovered(true),
            onClose: () => setIsHovered(false),
            // 기본값(true)이면 클릭하는 순간 닫혀 토글이 성립하지 않는다.
            closeOnClick: false,
          }
        : {})}
      backgroundColor={"#444440"}
      // padding 0으로 확대 이미지가 컨테이너를 꽉 채우므로, 툴팁 모서리를
      // 이미지와 같은 8px로 맞춰 배경이 네 모서리에 삐져나오지 않게 한다.
      // (화살표가 콘텐츠 박스 밖 자식이라 overflow: hidden은 주면 안 된다)
      borderRadius={"8px"}
      boxShadow={"0px 4px 8px 0px rgba(0, 0, 0, 0.20)"}
      maxWidth={"auto"}
      padding={"0"}
      margin={"0"}
      // 팝업이 화면 밖으로 나가면 8px 여유를 두고 위치를 보정한다.
      modifiers={[{ name: "preventOverflow", options: { padding: 8 } }]}
      label={
        <Box
          position={"relative"}
          // 툴팁 포지셔너에 pointerEvents: none 이 걸려 있어 확대 이미지 위 클릭이
          // 아래 카드로 통과해 엉뚱한 상품이 선택된다. 고정 중에만 되살려 가로챈다.
          pointerEvents={isPinned ? "auto" : "none"}
          cursor={isPinned ? "zoom-out" : undefined}
          onClick={(event) => {
            event.stopPropagation();
            closePin();
          }}
        >
          <Image
            src={imageUrl}
            boxSize={ZOOM_IMAGE_SIZE}
            objectFit={"contain"}
            background={"#FFFFFF"}
            borderRadius={"8px"}
            alt={alt}
          />
          {isAiCollected && <PathPickAiImageBadge />}
        </Box>
      }
    >
      {trigger}
    </Tooltip>
  );
}
